/**
 * Custom confirm / prompt / alert dialogs — a drop-in replacement for the
 * browser's blocking window.confirm/prompt/alert (which we never use).
 *
 * Promise-based imperative API, mirroring the contract of the natives so
 * call sites change minimally:
 *
 *   const dialog = useDialog();
 *
 *   // confirm → resolves true / false
 *   if (!(await dialog.confirm({ message: '…', confirmLabel: '…', danger: true }))) return;
 *
 *   // prompt → resolves the string, or null if cancelled (like window.prompt)
 *   const name = await dialog.prompt({ message: '…', defaultValue: 'x' });
 *   if (name == null) return;
 *
 *   // alert → resolves when dismissed
 *   await dialog.alert({ title: '…', message: '…' });
 *
 * Options: { title, message, confirmLabel, cancelLabel, danger, placeholder,
 *            defaultValue, dir, validate }. `validate(value)` (prompt only)
 *            returns true to accept or a string error to show inline.
 *
 * Mounted once near the app root (see main.jsx). Renders through a portal
 * into <body> using the shared `.modal-backdrop` / `.modal` styles.
 */
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useT } from './i18n.jsx';

const DialogCtx = createContext(null);

export function DialogProvider({ children }) {
  const [state, setState] = useState(null);
  // Holds the resolve() of the currently-open dialog's promise.
  const resolverRef = useRef(null);

  const settle = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    if (resolve) resolve(result);
  }, []);

  const request = useCallback((config) => new Promise((resolve) => {
    // If a dialog is somehow already open, resolve it as cancelled so its
    // awaiter isn't left hanging, then take over.
    if (resolverRef.current) {
      const prev = resolverRef.current;
      resolverRef.current = null;
      prev(config.kind === 'prompt' ? null : false);
    }
    resolverRef.current = resolve;
    setState(config);
  }), []);

  const api = useMemo(() => ({
    confirm: (opts = {}) => request({ kind: 'confirm', ...opts }),
    prompt: (opts = {}) => request({ kind: 'prompt', ...opts }),
    alert: (opts = {}) => request({ kind: 'alert', ...opts }),
  }), [request]);

  return (
    <DialogCtx.Provider value={api}>
      {children}
      <DialogHost state={state} onSettle={settle} />
    </DialogCtx.Provider>
  );
}

export function useDialog() {
  const v = useContext(DialogCtx);
  if (!v) throw new Error('useDialog() must be inside <DialogProvider>');
  return v;
}

function DialogHost({ state, onSettle }) {
  const t = useT();
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const acceptBtnRef = useRef(null);

  // Seed the input + clear errors synchronously when a dialog opens so the
  // prompt never flashes an empty field before its default value lands.
  useLayoutEffect(() => {
    if (!state) return;
    setValue(state.kind === 'prompt' ? (state.defaultValue ?? '') : '');
    setError(null);
  }, [state]);

  // Focus the input (prompt) or the accept button (confirm/alert) once the
  // portal has painted.
  useEffect(() => {
    if (!state) return;
    const raf = requestAnimationFrame(() => {
      if (state.kind === 'prompt') {
        inputRef.current?.focus();
        inputRef.current?.select();
      } else {
        acceptBtnRef.current?.focus();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [state]);

  // Esc cancels, regardless of focus.
  useEffect(() => {
    if (!state) return;
    const cancelResult = state.kind === 'prompt' ? null : false;
    const onKey = (e) => { if (e.key === 'Escape') onSettle(cancelResult); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, onSettle]);

  if (!state) return null;

  const { kind } = state;
  const cancel = () => onSettle(kind === 'prompt' ? null : false);
  const accept = () => {
    if (kind === 'prompt') {
      if (typeof state.validate === 'function') {
        const res = state.validate(value);
        if (res !== true) {
          setError(typeof res === 'string' && res ? res : t('common.invalidInput'));
          return;
        }
      }
      onSettle(value);
      return;
    }
    onSettle(true);
  };

  const confirmLabel = state.confirmLabel
    || (kind === 'confirm' ? t('common.confirm')
      : kind === 'prompt' ? t('common.save')
        : t('common.ok'));
  const cancelLabel = state.cancelLabel || t('common.cancel');
  const title = state.title || (kind === 'confirm' ? t('common.confirmTitle') : '');

  return createPortal(
    <div className="modal-backdrop" onClick={cancel}>
      <form
        className="modal modal-dialog"
        role={kind === 'alert' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); accept(); }}
      >
        <div className="modal-dialog-body">
          {title && <h3 className="modal-dialog-title">{title}</h3>}
          {state.message && <p className="modal-dialog-message">{state.message}</p>}
          {kind === 'prompt' && (
            <>
              <input
                ref={inputRef}
                className={`input${error ? ' invalid' : ''}`}
                value={value}
                onChange={(e) => { setValue(e.target.value); if (error) setError(null); }}
                placeholder={state.placeholder || ''}
                dir={state.dir || undefined}
                spellCheck={false}
              />
              {error && <div className="modal-dialog-error">{error}</div>}
            </>
          )}
        </div>
        <div className="modal-dialog-foot">
          {kind !== 'alert' && (
            <button type="button" className="btn ghost" onClick={cancel}>{cancelLabel}</button>
          )}
          <button
            type="submit"
            ref={acceptBtnRef}
            className={`btn ${state.danger ? 'danger' : 'primary'}`}
          >
            {confirmLabel}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
